<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 px-6 py-4">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Cross Sell</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage cross sell offerings</p>
                                </div>
                                <div class="flex items-center gap-2">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                :disabled="isLoading"
                                                @click="loadData"
                                        >
                                                <svg class="w-4 h-4" :class="{ 'animate-spin': isLoading }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Refresh
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 px-4 py-2.5 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer hover:bg-orange-6"
                                                @click="showCreateModal = true"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                </svg>
                                                Create Cross Sell
                                        </button>
                                </div>
                        </div>
                </header>

                <!-- Tab Bar -->
                <div class="bg-white border-b border-gray-200">
                    <div class="flex px-6 gap-0">
                        <button
                            v-for="tab in mainTabs"
                            :key="tab.id"
                            type="button"
                            class="py-3 px-6 text-sm font-medium border-none bg-transparent cursor-pointer border-b-2 -mb-px"
                            :class="{
                                'text-orange-7 border-orange-7': activeMainTab === tab.id,
                                'text-gray-500 border-transparent hover:text-gray-700': activeMainTab !== tab.id
                            }"
                            @click="activeMainTab = tab.id"
                        >
                            {{ tab.label }}
                        </button>
                    </div>
                </div>

                <!-- Main Content -->
                <main v-if="activeMainTab === 'list'" class="p-6">
                        <!-- Search and Filters Card -->
                        <div class="bg-white rounded-xl shadow-sm px-6 py-4 mb-4">
                                <div class="flex flex-wrap gap-4 items-center">
                                        <div class="flex-1 min-w-48">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search cross sells..."
                                                                class="w-full py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                @input="applyFilters"
                                                        >
                                                </div>
                                        </div>
                                        <div class="flex items-center gap-3">
                                                <label class="text-sm text-gray-700 font-medium">Published:</label>
                                                <select
                                                        v-model="publishedFilter"
                                                        class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none min-w-28 focus:border-orange-7"
                                                        @change="applyFilters"
                                                >
                                                        <option :value="null">All</option>
                                                        <option :value="true">Yes</option>
                                                        <option :value="false">No</option>
                                                </select>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <span class="text-xs bg-orange-2 text-orange-7 px-3 py-1 rounded-full font-medium">
                                                        {{ filteredItems.length }} cross sells
                                                </span>
                                        </div>
                                </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading cross sells...</span>
                        </div>

                        <!-- Table Card -->
                        <div v-else class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="overflow-x-auto">
                                        <table class="w-full border-collapse">
                                                <thead>
                                                        <tr class="bg-gray-50">
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-20">ID</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Name</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-24">Published</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Translations</th>
                                                                <th class="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-32">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        <tr v-if="paginatedItems.length === 0">
                                                                <td colspan="5" class="text-center py-12 px-4">
                                                                        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                                        </svg>
                                                                        <p class="text-gray-500 text-sm m-0">No cross sells found</p>
                                                                        <p class="text-gray-400 text-xs mt-1 mb-0">Create a new cross sell to get started</p>
                                                                </td>
                                                        </tr>
                                                        <template v-for="item in paginatedItems" :key="item.id">
                                                                <tr
                                                                        class="border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                                                                        @click="editItem(item.id)"
                                                                >
                                                                        <td class="py-3 px-4 text-sm text-gray-500 font-mono">{{ item.id }}</td>
                                                                        <td class="py-3 px-4 text-sm text-gray-900 font-medium">{{ item.name }}</td>
                                                                        <td class="py-3 px-4 text-center">
                                                                                <span
                                                                                        v-if="item.published"
                                                                                        class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                                                                >
                                                                                        Yes
                                                                                </span>
                                                                                <span
                                                                                        v-else
                                                                                        class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500"
                                                                                >
                                                                                        No
                                                                                </span>
                                                                        </td>
                                                                        <td class="py-3 px-4 text-center" @click.stop>
                                                                                <div class="flex items-center justify-center gap-2">
                                                                                        <span
                                                                                                class="inline-flex items-center py-1 px-2 text-xs font-medium rounded-full"
                                                                                                :class="getTranslationStatusClass(item.id)"
                                                                                                :title="getTranslationStatusTitle(item.id)"
                                                                                        >
                                                                                                {{ getTranslationStatusLabel(item.id) }}
                                                                                        </span>
                                                                                        <button
                                                                                                type="button"
                                                                                                class="p-1 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7 hover:bg-orange-1"
                                                                                                title="Expand translations"
                                                                                                @click.stop="toggleTranslationExpand(item.id)"
                                                                                        >
                                                                                                <svg 
                                                                                                        class="w-4 h-4 transition-transform"
                                                                                                        :class="expandedRowIds.has(item.id) ? 'rotate-180' : ''"
                                                                                                        fill="none" 
                                                                                                        viewBox="0 0 24 24" 
                                                                                                        stroke="currentColor" 
                                                                                                        stroke-width="2"
                                                                                                >
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                                                                                                </svg>
                                                                                        </button>
                                                                                </div>
                                                                        </td>
                                                                        <td class="py-3 px-4 text-right">
                                                                                <div class="flex justify-end gap-2">
                                                                                        <button
                                                                                                type="button"
                                                                                                class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7"
                                                                                                title="Edit"
                                                                                                @click.stop="editItem(item.id)"
                                                                                        >
                                                                                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                                </svg>
                                                                                        </button>
                                                                                        <button
                                                                                                type="button"
                                                                                                class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-red-600"
                                                                                                title="Delete"
                                                                                                @click.stop="confirmDelete(item)"
                                                                                        >
                                                                                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                                </svg>
                                                                                        </button>
                                                                                </div>
                                                                        </td>
                                                                </tr>
                                                                <!-- Expanded Translation Row -->
                                                                <tr v-if="expandedRowIds.has(item.id)" class="bg-gray-50">
                                                                        <td colspan="7" class="p-0">
                                                                                <div class="p-4 border-t border-gray-200">
                                                                                        <InlineTranslationEditor
                                                                                                :item-id="item.id"
                                                                                                :fields="translationFields"
                                                                                                :languages="languages"
                                                                                                :selected-language-id="selectedLanguageId"
                                                                                                :is-loading="isLoadingTranslations"
                                                                                                :is-saving="isSavingTranslations"
                                                                                                :get-field="getTranslationField"
                                                                                                :get-language-status="getLanguageTranslationStatus"
                                                                                                :has-unsaved-changes="hasUnsavedChanges"
                                                                                                @update:selected-language-id="selectedLanguageId = $event"
                                                                                                @update-field="updateTranslationField(item.id, $event.langId, $event.key, $event.value)"
                                                                                                @save="saveTranslations(item.id)"
                                                                                                @cancel="cancelTranslationEdit(item.id)"
                                                                                        />
                                                                                </div>
                                                                        </td>
                                                                </tr>
                                                        </template>
                                                </tbody>
                                        </table>
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="flex justify-between items-center px-6 py-4 border-t border-gray-200">
                                        <div class="text-sm text-gray-500">
                                                Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredItems.length) }} of {{ filteredItems.length }} cross sells
                                        </div>
                                        <div class="flex gap-2">
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage--"
                                                >
                                                        Previous
                                                </button>
                                                <button
                                                        v-for="page in visiblePages"
                                                        :key="page"
                                                        type="button"
                                                        class="py-2 px-3 border rounded-md text-sm cursor-pointer min-w-10"
                                                        :class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300'"
                                                        @click="currentPage = page"
                                                >
                                                        {{ page }}
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage++"
                                                >
                                                        Next
                                                </button>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Order Tab -->
                <main v-if="activeMainTab === 'order'" class="p-6">
                    <!-- Loading State -->
                    <div v-if="isLoadingOrder" class="flex justify-center items-center py-16 px-4">
                        <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span class="ml-3 text-gray-500 text-sm">Loading cross sells...</span>
                    </div>

                    <template v-else>
                        <!-- Info bar -->
                        <div class="flex justify-between items-center mb-4">
                            <div class="flex items-center gap-3">
                                <span class="text-xs bg-orange-2 text-orange-7 px-3 py-1 rounded-full font-medium">
                                    {{ orderItems.length }} published cross sells
                                </span>
                                <span class="text-sm text-gray-500">Drag items to reorder</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <div v-if="isSavingOrder" class="flex items-center gap-2 text-sm text-orange-7">
                                    <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Saving order...
                                </div>
                            </div>
                        </div>

                        <!-- Empty State -->
                        <div v-if="orderItems.length === 0" class="bg-white rounded-xl shadow-sm py-16 px-4 text-center">
                            <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                            <p class="text-gray-500 text-sm m-0">No published cross sells</p>
                            <p class="text-gray-400 text-xs mt-1 mb-0">Publish cross sells to manage their order</p>
                        </div>

                        <!-- Draggable Gallery -->
                        <draggable
                            v-else
                            v-model="orderItems"
                            item-key="id"
                            handle=".drag-handle"
                            ghost-class="opacity-50"
                            class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
                            @end="onReorderCrossSells"
                        >
                            <template #item="{ element, index }">
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 hover:shadow-md transition-shadow">
                                    <div class="relative">
                                        <div class="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
                                            <img
                                                v-if="orderThumbnails[element.crosssellid]"
                                                :src="orderThumbnails[element.crosssellid]"
                                                :alt="element.name"
                                                class="w-full h-full object-contain"
                                            >
                                            <svg v-else class="w-12 h-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                                            </svg>
                                        </div>
                                        <div class="absolute top-2 left-2 bg-white/90 text-gray-700 text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center shadow-sm">
                                            {{ index + 1 }}
                                        </div>
                                        <div class="drag-handle absolute top-2 right-2 bg-white/90 rounded-md p-1 cursor-grab text-gray-400 hover:text-gray-600 shadow-sm">
                                            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 8h16M4 16h16" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div class="p-3">
                                        <p class="text-sm font-medium text-gray-900 m-0 truncate">{{ element.name }}</p>
                                        <p class="text-xs text-gray-500 mt-1 mb-0">ID: {{ element.id }}</p>
                                    </div>
                                </div>
                            </template>
                        </draggable>
                    </template>
                </main>

                <!-- Create Modal -->
                <div v-if="showCreateModal" class="fixed inset-0 z-50 flex items-center justify-center">
                        <div class="absolute inset-0 bg-black/50" @click="closeCreateModal"></div>
                        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-md m-4">
                                <div class="p-6">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0 mb-6">Create New Cross Sell</h3>
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                                <input
                                                        v-model="newItemName"
                                                        type="text"
                                                        placeholder="Enter cross sell name"
                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                >
                                        </div>
                                        <div class="mb-6">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Cross Sell ID (Offering)</label>
                                                <div v-if="createSelectedOffering" class="flex items-center gap-2 py-2.5 px-3 border border-gray-300 rounded-lg bg-white">
                                                        <div class="flex-1 min-w-0">
                                                                <div class="text-sm text-gray-900 truncate">{{ createSelectedOffering.name }}</div>
                                                        </div>
                                                        <button
                                                                type="button"
                                                                class="flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 border-none bg-transparent cursor-pointer"
                                                                @click="clearCreateOffering"
                                                        >
                                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                        </button>
                                                </div>
                                                <div v-else class="relative">
                                                        <input
                                                                v-model="createOfferingSearch"
                                                                type="text"
                                                                placeholder="Search offerings by ID, size, or description... (optional)"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                @focus="createOfferingSearchOpen = true"
                                                                @input="createOfferingSearchOpen = true"
                                                                @blur="closeCreateOfferingSearch"
                                                        >
                                                        <div
                                                                v-if="createOfferingSearchOpen && filteredCreateOfferings.length > 0"
                                                                class="absolute z-10 mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg"
                                                        >
                                                                <button
                                                                        v-for="offering in filteredCreateOfferings"
                                                                        :key="offering.id"
                                                                        type="button"
                                                                        class="w-full text-left py-2 px-3 hover:bg-orange-50 border-none bg-transparent cursor-pointer border-b border-b-gray-100 last:border-b-0"
                                                                        @mousedown.prevent="selectCreateOffering(offering)"
                                                                >
                                                                        <div class="text-sm text-gray-900">{{ offering.id }} | {{ offering.size }} - {{ offering.description }}</div>
                                                                </button>
                                                        </div>
                                                        <div
                                                                v-else-if="createOfferingSearchOpen && createOfferingSearch.trim() && filteredCreateOfferings.length === 0"
                                                                class="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-3 px-4 text-sm text-gray-500 text-center"
                                                        >
                                                                No offerings found
                                                        </div>
                                                </div>
                                        </div>
                                        <div class="flex justify-end gap-3">
                                                <button
                                                        type="button"
                                                        class="py-2.5 px-4 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-50"
                                                        @click="closeCreateModal"
                                                >
                                                        Cancel
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="!newItemName.trim() || isCreating"
                                                        class="py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50 hover:bg-orange-6"
                                                        @click="createItem"
                                                >
                                                        {{ isCreating ? 'Creating...' : 'Create' }}
                                                </button>
                                        </div>
                                </div>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch, Ref } from 'vue';
import { useRouter, Router } from 'vue-router';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import CrossSellService from 'services/CrossSellService';
import OfferingService from 'services/OfferingService';
import LanguageService from 'services/LanguageService';
import { httpClient } from 'utils/http';
import type { CrossSellModel, OfferingModel, LanguageModel } from '@sosocio/creator/interfaces';
import draggable from 'vuedraggable';
import InlineTranslationEditor from 'components/InlineTranslationEditor.vue';
import { useInlineTranslations } from 'composables/useInlineTranslations';
import type { TranslationFieldSchema, TranslationEntry, TranslationStatus } from 'composables/useInlineTranslations';

interface CrossSellWithTranslations extends CrossSellModel {
        header?: string;
        description?: string;
}

const translationFields: TranslationFieldSchema[] = [
        { key: 'header', label: 'Header', type: 'text', placeholder: 'Enter header translation' },
        { key: 'description', label: 'Description', type: 'textarea', rows: 3, placeholder: 'Enter description translation' },
];

const getCrossSellLanguageStatus = (entry: TranslationEntry): TranslationStatus => {
        const header = (entry.header || '').trim();
        const description = (entry.description || '').trim();
        if (header) return 'complete';
        if (description) return 'partial';
        return 'none';
};

export default defineComponent({
        name: 'CrossSellsPage',
        components: { draggable, InlineTranslationEditor },
        setup() {
                const router: Router = useRouter();
                const mainTabs = [
                    { id: 'list', label: 'List' },
                    { id: 'order', label: 'Order' },
                ];

                const items: Ref<CrossSellWithTranslations[]> = ref([]);
                const offerings: Ref<OfferingModel[]> = ref([]);
                const languages: Ref<LanguageModel[]> = ref([]);

                const notify = {
                        success: (title: string): void => {
                                Swal.fire({ toast: true, position: 'top-end', icon: 'success' as SweetAlertIcon, title, showConfirmButton: false, timer: 3000 });
                        },
                        error: (title: string): void => {
                                Swal.fire({ toast: true, position: 'top-end', icon: 'error' as SweetAlertIcon, title, showConfirmButton: false, timer: 3000 });
                        },
                };

                const {
                        translations,
                        expandedRowIds,
                        selectedLanguageId,
                        isLoadingTranslations,
                        isSavingTranslations,
                        loadAll: loadAllTranslations,
                        toggleExpand: toggleTranslationExpand,
                        getField: getTranslationField,
                        updateField: updateTranslationField,
                        hasUnsavedChanges,
                        getLanguageTranslationStatus,
                        getTranslationStatus,
                        getTranslationStatusClass,
                        getTranslationStatusLabel,
                        getTranslationStatusTitle,
                        saveTranslations,
                        cancelTranslationEdit,
                        initSelectedLanguage,
                } = useInlineTranslations({
                        namespace: 'cross_sell',
                        fields: translationFields,
                        languages,
                        getLanguageStatus: getCrossSellLanguageStatus,
                        notify,
                });

                const searchQuery: Ref<string> = ref('');
                const publishedFilter: Ref<boolean | null> = ref(true);
                const currentPage: Ref<number> = ref(1);
                const pageSize: number = 20;
                const isLoading: Ref<boolean> = ref(true);
                const showCreateModal: Ref<boolean> = ref(false);
                const newItemName: Ref<string> = ref('');
                const newItemCrossSellId: Ref<number | null> = ref(null);
                const isCreating: Ref<boolean> = ref(false);
                const createOfferingSearch: Ref<string> = ref('');
                const createOfferingSearchOpen: Ref<boolean> = ref(false);
                const createSelectedOffering = ref<{ id: number; name: string; externalid: string } | null>(null);
                const activeMainTab: Ref<string> = ref('list');
                const orderItems: Ref<CrossSellWithTranslations[]> = ref([]);
                const orderThumbnails: Ref<Record<number, string>> = ref({});
                const isLoadingOrder: Ref<boolean> = ref(false);
                const isSavingOrder: Ref<boolean> = ref(false);

                const filteredItems = computed<CrossSellWithTranslations[]>(() => {
                        let result = items.value;
                        
                        if (publishedFilter.value !== null) {
                                const filterValue = publishedFilter.value ? 1 : 0;
                                result = result.filter((item: CrossSellWithTranslations) => item.published === filterValue);
                        }
                        
                        if (searchQuery.value.trim()) {
                                const query = searchQuery.value.toLowerCase();
                                result = result.filter((item: CrossSellWithTranslations) =>
                                        String(item.name ?? '').toLowerCase().includes(query) ||
                                        String(item.id).includes(query)
                                );
                        }
                        
                        return result;
                });

                const totalPages = computed<number>(() => Math.ceil(filteredItems.value.length / pageSize));

                const paginatedItems = computed<CrossSellWithTranslations[]>(() => {
                        const start = (currentPage.value - 1) * pageSize;
                        return filteredItems.value.slice(start, start + pageSize);
                });

                const visiblePages = computed<number[]>(() => {
                        const pages: number[] = [];
                        const maxVisible = 5;
                        let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
                        let end = Math.min(totalPages.value, start + maxVisible - 1);
                        if (end - start + 1 < maxVisible) {
                                start = Math.max(1, end - maxVisible + 1);
                        }
                        for (let i = start; i <= end; i++) {
                                pages.push(i);
                        }
                        return pages;
                });

                const applyFilters = (): void => {
                        currentPage.value = 1;
                };

                const loadData = async (): Promise<void> => {
                        isLoading.value = true;
                        try {
                                const [crossSellsResponse, offeringsResponse, languagesResponse] = await Promise.all([
                                        CrossSellService.getAll(new URLSearchParams({ limit: '0' })),
                                        OfferingService.getAll(new URLSearchParams({ limit: '0', fields: 'id,size,description' })),
                                        LanguageService.getAll(new URLSearchParams({ limit: '0' })),
                                ]);
                                items.value = crossSellsResponse;
                                offerings.value = offeringsResponse;
                                languages.value = languagesResponse;

                                if (languages.value.length > 0) {
                                        initSelectedLanguage();
                                        await loadAllTranslations(items.value);
                                }
                        } catch (error: unknown) {
                                console.error('Failed to load data:', error);
                                const icon: SweetAlertIcon = 'error';
                                Swal.fire({
                                        title: 'Error',
                                        text: 'Failed to load data',
                                        icon,
                                });
                        } finally {
                                isLoading.value = false;
                        }
                };

                const editItem = (id: number): void => {
                        router.push(`/crosssell/${id}`);
                };

                const confirmDelete = async (item: CrossSellWithTranslations): Promise<void> => {
                        const result = await Swal.fire({
                                title: 'Delete Cross Sell?',
                                text: `Are you sure you want to delete "${item.name}"? This action cannot be undone.`,
                                icon: 'warning' as SweetAlertIcon,
                                showCancelButton: true,
                                confirmButtonColor: '#dc2626',
                                cancelButtonColor: '#6b7280',
                                confirmButtonText: 'Yes, delete it',
                                cancelButtonText: 'Cancel',
                        });

                        if (result.isConfirmed) {
                                try {
                                        await CrossSellService.delete(item.id);
                                        items.value = items.value.filter((i: CrossSellWithTranslations) => i.id !== item.id);
                                        Swal.fire({
                                                toast: true,
                                                position: 'top-end',
                                                icon: 'success' as SweetAlertIcon,
                                                title: 'Cross sell deleted successfully',
                                                showConfirmButton: false,
                                                timer: 3000,
                                        });
                                } catch (error: unknown) {
                                        const errorMessage = error instanceof Error ? error.message : 'Failed to delete cross sell';
                                        Swal.fire({
                                                title: 'Error',
                                                text: errorMessage,
                                                icon: 'error' as SweetAlertIcon,
                                        });
                                }
                        }
                };

                const filteredCreateOfferings = computed(() => {
                        const query = createOfferingSearch.value.toLowerCase().trim();
                        if (!query) return offerings.value.slice(0, 50);
                        return offerings.value.filter((o: OfferingModel) =>
                                String(o.description || '').toLowerCase().includes(query) ||
                                String(o.size || '').toLowerCase().includes(query) ||
                                String(o.id || '').includes(query)
                        ).slice(0, 50);
                });

                const selectCreateOffering = (offering: OfferingModel): void => {
                        newItemCrossSellId.value = offering.id;
                        let label = `${offering.id}`;
                        if (offering.size) label += ` | ${offering.size}`;
                        if (offering.description) label += ` - ${offering.description}`;
                        createSelectedOffering.value = { id: offering.id, name: label, externalid: '' };
                        createOfferingSearch.value = '';
                        createOfferingSearchOpen.value = false;
                };

                const clearCreateOffering = (): void => {
                        newItemCrossSellId.value = null;
                        createSelectedOffering.value = null;
                        createOfferingSearch.value = '';
                        createOfferingSearchOpen.value = false;
                };

                const closeCreateOfferingSearch = (): void => {
                        window.setTimeout(() => { createOfferingSearchOpen.value = false; }, 200);
                };

                const closeCreateModal = (): void => {
                        showCreateModal.value = false;
                        newItemName.value = '';
                        newItemCrossSellId.value = null;
                        createSelectedOffering.value = null;
                        createOfferingSearch.value = '';
                        createOfferingSearchOpen.value = false;
                };

                const createItem = async (): Promise<void> => {
                        if (!newItemName.value.trim()) return;

                        isCreating.value = true;
                        try {
                                const newCrossSell = await CrossSellService.create({
                                        name: newItemName.value.trim(),
                                        crosssellid: newItemCrossSellId.value ?? undefined,
                                });
                                closeCreateModal();
                                router.push(`/crosssell/${newCrossSell.id}`);
                        } catch (error: unknown) {
                                const errorMessage = error instanceof Error ? error.message : 'Failed to create cross sell';
                                Swal.fire({
                                        title: 'Error',
                                        text: errorMessage,
                                        icon: 'error' as SweetAlertIcon,
                                });
                        } finally {
                                isCreating.value = false;
                        }
                };

                const loadOrderData = async (): Promise<void> => {
                    isLoadingOrder.value = true;
                    try {
                        const params = new URLSearchParams({
                            limit: '0',
                            where: JSON.stringify({ published: 1 }),
                        });
                        const data = await CrossSellService.getAll(params);
                        orderItems.value = (data as CrossSellWithTranslations[]).sort(
                            (a: CrossSellWithTranslations, b: CrossSellWithTranslations) => 
                                ((a as any).serialnumber || 0) - ((b as any).serialnumber || 0)
                        );

                        const crossSellIds = orderItems.value
                            .map((item: CrossSellWithTranslations) => item.crosssellid)
                            .filter((id: number | undefined): id is number => id != null && id > 0);

                        const uniqueIds = [...new Set(crossSellIds)];
                        const thumbnails: Record<number, string> = {};
                        
                        await Promise.all(
                            uniqueIds.map(async (offeringId: number) => {
                                try {
                                    const { data } = await httpClient.get(`/api/offering/${offeringId}`);
                                    if (data?.thumbnail) {
                                        thumbnails[offeringId] = data.thumbnail;
                                    }
                                } catch (error: unknown) {
                                    console.error(`Failed to load offering ${offeringId}:`, error);
                                }
                            })
                        );
                        
                        orderThumbnails.value = thumbnails;
                    } catch (error: unknown) {
                        const errorMessage = error instanceof Error ? error.message : 'Failed to load cross sells';
                        Swal.fire({
                            title: 'Error',
                            text: errorMessage,
                            icon: 'error' as SweetAlertIcon,
                        });
                    } finally {
                        isLoadingOrder.value = false;
                    }
                };

                const onReorderCrossSells = async (): Promise<void> => {
                    const hasChanged = orderItems.value.some((item: CrossSellWithTranslations, index: number) => {
                        const expectedSerial = index + 1;
                        return ((item as any).serialnumber || 0) !== expectedSerial;
                    });

                    if (!hasChanged) {
                        return;
                    }

                    const previousOrder = orderItems.value.map((item: CrossSellWithTranslations) => ({ ...item }));
                    isSavingOrder.value = true;

                    try {
                        const updates = orderItems.value.map((item: CrossSellWithTranslations, index: number) => ({
                            ...item,
                            serialnumber: index + 1,
                        }));
                        orderItems.value = updates;

                        await Promise.all(
                            updates.map((item: CrossSellWithTranslations) =>
                                CrossSellService.update(item.id!, { serialnumber: (item as any).serialnumber })
                            )
                        );

                        Swal.fire({
                            toast: true,
                            position: 'top-end',
                            icon: 'success' as SweetAlertIcon,
                            title: 'Order saved',
                            showConfirmButton: false,
                            timer: 2000,
                        });
                    } catch (error: unknown) {
                        console.error('Failed to save order:', error);
                        orderItems.value = previousOrder as CrossSellWithTranslations[];
                        Swal.fire({
                            title: 'Error',
                            text: 'Failed to save order. Changes reverted.',
                            icon: 'error' as SweetAlertIcon,
                        });
                    } finally {
                        isSavingOrder.value = false;
                    }
                };

                watch(activeMainTab, (newTab: string): void => {
                    if (newTab === 'order') {
                        loadOrderData();
                    }
                });

                onMounted((): void => {
                        loadData();
                });

                return {
                        translationFields,
                        items,
                        offerings,
                        languages,
                        translations,
                        expandedRowIds,
                        selectedLanguageId,
                        isLoadingTranslations,
                        isSavingTranslations,
                        searchQuery,
                        publishedFilter,
                        currentPage,
                        pageSize,
                        isLoading,
                        showCreateModal,
                        newItemName,
                        newItemCrossSellId,
                        isCreating,
                        createOfferingSearch,
                        createOfferingSearchOpen,
                        createSelectedOffering,
                        filteredCreateOfferings,
                        selectCreateOffering,
                        clearCreateOffering,
                        closeCreateOfferingSearch,
                        closeCreateModal,
                        filteredItems,
                        totalPages,
                        paginatedItems,
                        visiblePages,
                        applyFilters,
                        loadData,
                        toggleTranslationExpand,
                        getTranslationField,
                        updateTranslationField,
                        hasUnsavedChanges,
                        getLanguageTranslationStatus,
                        getTranslationStatus,
                        getTranslationStatusClass,
                        getTranslationStatusLabel,
                        getTranslationStatusTitle,
                        saveTranslations,
                        cancelTranslationEdit,
                        editItem,
                        confirmDelete,
                        createItem,
                        mainTabs,
                        activeMainTab,
                        orderItems,
                        orderThumbnails,
                        isLoadingOrder,
                        isSavingOrder,
                        onReorderCrossSells,
                };
        },
});
</script>
